/**
 * gamba-subscription-config.js — el ÚNICO lugar del theme donde viven los tres
 * valores de la app de suscripciones y sus fallbacks.
 *
 * Fuente del contrato: docs/integracion-suscripciones.md, "Leer la configuración
 * pública". `GET /apps/suscripcion/api/config` devuelve tres cosas: el porcentaje
 * de descuento, el umbral de envío gratis y las frecuencias con sus etiquetas.
 * Cacheable 5 minutos.
 *
 * POR QUÉ ES JS Y NO LIQUID: Liquid no puede llamar a un endpoint. El primer
 * pintado lo hace Liquid con los fallbacks y este módulo HIDRATA después. Y por
 * qué los valores no se escriben a mano en ninguna sección: Gamba los edita en el
 * backoffice (Apps → Gamba Suscripciones → Configuración) y la tienda tiene que
 * reflejarlos SIN DEPLOY. Son fallback, no fuente.
 *
 * ---------------------------------------------------------------------------
 * LA API — es chica a propósito, y la consumen dos tareas: el panel de la PDP
 * (`gamba-purchase-mode.js`) y el botón del carrito (`gamba-cart-subscribe.js`).
 *
 *   window.GambaSubscriptionConfig.fallback        // los valores horneados, congelado
 *   window.GambaSubscriptionConfig.read()          // SÍNCRONO. Lo mejor que se sabe hoy
 *   window.GambaSubscriptionConfig.load()          // Promise. Hidrata. NUNCA rechaza
 *   window.GambaSubscriptionConfig.monthsLabel()   // "cada 3 meses" desde los strings de Liquid
 *
 * Los tres primeros devuelven / son un objeto con esta forma exacta (`monthsLabel`
 * es aparte: recibe un número y dos strings, y devuelve texto):
 *
 *   {
 *     discountPercent: 10,           // number 0..100. El `XX% OFF` de los tiles sale de acá
 *     freeShippingAmountCents: 4500000, // number en CENTAVOS. SUBTOTAL del carrito ANTES del descuento. 0 = no hay envío gratis
 *     maxShippingAmountCents: 2000000, // number en CENTAVOS. El tope de envío que la app acepta
 *     frequencies: [                 // ordenadas por count, sin repetidos, al menos una
 *       { count: 1, label: '1 mes' },
 *       { count: 2, label: null }    // label null = traducilo vos con `count` (ver abajo)
 *     ],
 *     source: 'app' | 'fallback'     // de dónde salió. Para debuggear un número que está mal
 *   }
 *
 * El objeto viene CONGELADO (`Object.freeze`), el array de frecuencias también:
 * los dos consumidores comparten la misma instancia y la misma caché. Si tenés que
 * ordenar o mapear, copiá primero (`[...config.frequencies]`).
 *
 * CÓMO SE CONSUME, en dos líneas:
 *
 *   1. La sección pinta el primer estado con los fallbacks (Liquid, con `| t`).
 *   2. El módulo del consumidor hidrata:
 *        window.GambaSubscriptionConfig?.load().then(applyConfig);
 *      El `?.` no es adorno: si alguien se olvida el `<script>` del config, el
 *      consumidor se queda con lo que pintó Liquid en vez de tirar TypeError.
 *
 * CÓMO SE CARGA: la sección/snippet consumidor emite su propio tag, ANTES del tag
 * de su módulo (los `type="module"` corren en orden de documento):
 *
 *     <script src="{{ 'gamba-subscription-config.js' | asset_url }}" type="module"></script>
 *     <script src="{{ 'gamba-purchase-mode.js' | asset_url }}" type="module"></script>
 *
 * Es el patrón de `sections/gamba-ideas-selector.liquid`. NO va en
 * `layout/theme.liquid` (el plan 03 dice explícitamente que el layout no se toca)
 * y NO se consume con `import`: no hay entrada en el importmap de
 * `snippets/scripts.liquid` y un import relativo (`./gamba-subscription-config.js`)
 * RESUELVE SIN EL `?v=` del `asset_url`. Medido el 2026-09-08 contra el CDN de la
 * tienda: esa URL sin versionar vuelve con `cache-control: public, max-age=31557600`
 * y `cf-cache-status: HIT`. O sea: un año de caché sobre el archivo que más va a
 * cambiar. Si algún día alguien agrega `"@gamba/subscription-config"` al importmap,
 * este archivo ya exporta los mismos símbolos y no hay que reescribirlo.
 *
 * LA DIVISIÓN DE TRABAJO, Y ES LA REGLA QUE ORDENA TODO ESTE ARCHIVO:
 *
 *   **QUÉ frecuencias existen lo dice el endpoint. CÓMO se dicen lo dice Liquid.**
 *
 * De acá salen los `count` habilitados y nada más. Las palabras las pone `| t`, que
 * es lo único que traduce y pluraliza, y viajan al DOM en dos strings —la forma
 * `one` y la forma `other` con el centinela `__COUNT__`— que `monthsLabel()`
 * combina con el número. Los consumidores CONSTRUYEN sus opciones desde
 * `config.frequencies`: no emiten un universo de doce y esconden diez.
 *
 * EL `label` QUE MANDA LA APP NO SE USA PARA MOSTRAR. Se normaliza y se conserva
 * en el par `{ count, label }` porque es parte de la respuesta, pero **ninguna
 * superficie lo pinta**, y la razón es dura: el backoffice lo escribe **sólo en
 * español**, así que usarlo rompería el par en/es que la tienda mantiene. Además
 * viene en una forma que no compone: el panel de la PDP arma "cada 1, 2 o 3 meses"
 * y los labels de la app dan "cada CADA MES, CADA 2 MESES". Del endpoint tomamos
 * QUÉ counts están habilitados; el texto es nuestro y traducido.
 *
 * Por eso los fallbacks vienen con `label: null`: este módulo no puede llamar a
 * `| t` y hornear `'1 mes'` en JS rompería el en/es. El consumidor traduce a partir
 * de `count`, que es el dato que la medición encontró faltando en el boceto
 * (`fase-3-b` §13.1: el boceto guarda `'1 MES'`, el label, y no el número). El par
 * es `{ count, label }`, nunca un string a parsear.
 * ---------------------------------------------------------------------------
 */

/** El App Proxy. Root-absoluto a propósito: el proxy no vive bajo el prefijo de locale. */
const ENDPOINT = '/apps/suscripcion/api/config';

/** "Cacheable 5 minutos", del documento de integración. */
const TTL_MS = 5 * 60 * 1000;

/** Corta el fetch colgado. Sin esto, un proxy lento deja la hidratación pendiente para siempre. */
const TIMEOUT_MS = 5000;

/** Clave de `sessionStorage`. Namespaceada para no chocar con nada de Horizon ni de una app. */
const CACHE_KEY = 'gamba:subscription-config';

/**
 * Los valores iniciales de docs/integracion-suscripciones.md. FALLBACK, NO FUENTE:
 * son lo que la app trae de fábrica, y existen sólo para el primer pintado y para
 * cuando el endpoint no responde. Si un número está mal en pantalla, se mira acá
 * y en el backoffice; no hay un tercer lugar.
 *
 * @typedef {{ count: number, label: string | null }} GambaFrequency
 * @typedef {{ discountPercent: number, freeShippingAmountCents: number, maxShippingAmountCents: number, frequencies: GambaFrequency[], source: 'app' | 'fallback' }} GambaSubscriptionConfigValue
 *
 * @type {Readonly<GambaSubscriptionConfigValue>}
 */
export const SUBSCRIPTION_CONFIG_FALLBACK = Object.freeze({
  // 10 % lineal sobre productos, igual para TODOS los tramos de suscripción.
  discountPercent: 10,
  /*
   * $45.000 en centavos: el SUBTOTAL DE PRODUCTOS a partir del cual la app no
   * cobra envío. Antes del descuento de suscripción.
   *
   * POR QUÉ MONTO Y NO UNIDADES — el cambio del 2026-09-16, y hay que dejarlo
   * escrito porque el nombre viejo (`freeShippingThreshold`, en unidades) va a
   * seguir apareciendo en cualquier copia vieja de este archivo: Gamba vende
   * una variante POR CANTIDAD ("6 unidades" es UNA variante con su precio de
   * pack, porque Shipnow necesita ver un solo ítem para pickearlo como pack).
   * Esa variante entra al carrito como `quantity: 1`, así que contando unidades
   * un carrito de 6 suma 1 y NUNCA llegaba al umbral: la grilla de precios
   * prometía envío gratis en las columnas de 6 y 12 y el carrito no lo daba.
   *
   * 0 ES UN VALOR VÁLIDO Y SIGNIFICA "NO HAY ENVÍO GRATIS", no "gratis
   * siempre". Cada consumidor lo guarda por su cuenta, porque un `>=` contra 0
   * da verdadero para cualquier carrito.
   */
  freeShippingAmountCents: 4500000,
  // $20.000 en centavos: el tope de envío que la app acepta para una
  // suscripción. Es fallback y no fuente — el valor vive en el backoffice y lo
  // sirve el endpoint.
  maxShippingAmountCents: 2000000,
  // 1, 2 y 3 meses. El rango que la app admite es 1 a 12.
  frequencies: Object.freeze([
    Object.freeze({ count: 1, label: null }),
    Object.freeze({ count: 2, label: null }),
    Object.freeze({ count: 3, label: null }),
  ]),
  source: 'fallback',
});

/**
 * Los nombres de campo que aceptamos del endpoint.
 *
 * K-15 SIN CERRAR: el contrato exacto lo fija el README del proyecto de la app,
 * que no está en esta repo, y el endpoint no se puede llamar hoy (la tienda tiene
 * password). Así que se aceptan los alias plausibles y **este objeto es el único
 * lugar donde se corrige** cuando el nombre real se conozca.
 *
 * El modo de falla que esto tapa a medias hay que decirlo derecho: si NINGÚN alias
 * coincide, caemos al fallback, que hoy tiene los MISMOS valores que la app trae de
 * fábrica — o sea que el error es invisible hasta que Gamba cambia un número en el
 * backoffice y la tienda no se mueve. Por eso, cuando el endpoint responde JSON y
 * no reconocemos nada, se emite un `console.warn` con las claves recibidas.
 */
const FIELDS = Object.freeze({
  discount: ['discount_percent', 'discountPercent', 'discount', 'subscription_discount_percent'],
  /*
   * EL UMBRAL, EN CENTAVOS. Desde el 2026-09-16 la app manda
   * `freeShippingAmountCents` y dejó de mandar `freeShippingThreshold` /
   * `freeShippingUnits`, que contaban UNIDADES (ver el fallback para el
   * porqué).
   *
   * LOS ALIAS EN UNIDADES NO ESTÁN EN ESTA LISTA, y sacarlos es parte del
   * arreglo, no limpieza: si la app llegara a mandar las dos formas durante la
   * transición, `pick` devuelve el PRIMER alias presente — un `freeShippingUnits: 6`
   * heredado ganaría, y 6 centavos deja todos los carritos con envío gratis.
   * Un nombre que no está no puede ganar.
   */
  amount: ['freeShippingAmountCents', 'free_shipping_amount_cents', 'free_shipping_cents'],
  // `allowedFrequencies` es EL nombre que la app manda hoy (mismo chequeo), y
  // manda una lista de NÚMEROS: `[1, 2, 3]`. `normalizeFrequency` ya acepta el
  // número pelado, así que sólo faltaba el nombre.
  frequencies: [
    'allowedFrequencies',
    'allowed_frequencies',
    'frequencies',
    'frequency_options',
    'frequencies_enabled',
    'intervals',
  ],
  // Los labels vienen APARTE y como objeto indexado por count
  // (`{"1":"Mensual","2":"Bimestral"}`), no como campo de cada item. Es la
  // tercera forma que hay que soportar y por eso tiene su propia entrada.
  frequencyLabels: ['frequencyLabels', 'frequency_labels', 'labels'],
  // EL TOPE DE ENVÍO, agregado por la app el 2026-09-10 a pedido de este lado:
  // hasta entonces el carrito lo tenía hardcodeado en $20.000 y se
  // desincronizaba del backoffice en silencio. Viene en CENTAVOS y por eso el
  // nombre lo dice: no es el mismo número que el cliente ve en pesos.
  // OJO — DESDE LA D-14 (fase 7) NADIE LEE ESTE VALOR EN EL THEME.
  // Lo leía `#overCap()` de `gamba-cart-subscribe.js` para avisar temprano que
  // el envío superaba el tope; el carrito dejó de cotizar el envío, así que ese
  // chequeo se fue y **el tope lo valida sólo la app**, que tiene su propio
  // error. Se sigue normalizando a propósito: el endpoint lo manda (la app lo
  // agregó el 2026-09-10 a pedido del theme), y borrar la normalización haría
  // que el día que vuelva un consumidor el alias se tenga que redescubrir.
  cap: ['maxShippingAmountCents', 'max_shipping_amount_cents', 'shipping_cap_cents'],
  count: ['count', 'months', 'month', 'interval_count', 'value'],
  label: ['label', 'name', 'title', 'text'],
});

/* ---------------------------------------------------------------------------
   Estado del módulo. Una sola caché, un solo fetch en vuelo.
   --------------------------------------------------------------------------- */

/** Lo último que se supo de la app, con su timestamp. `null` = todavía nada. */
/** @type {{ value: GambaSubscriptionConfigValue, savedAt: number } | null} */
let cached = null;

/** El fetch en vuelo, compartido: el panel y el carrito en la misma página piden UNA vez. */
/** @type {Promise<GambaSubscriptionConfigValue> | null} */
let inFlight = null;

/** El warn de forma inesperada se emite una vez por pageview, no una por consumidor. */
let warned = false;

/* ---------------------------------------------------------------------------
   Caché — memoria del módulo + sessionStorage
   --------------------------------------------------------------------------- */

/**
 * POR QUÉ `sessionStorage` Y NO SÓLO LA MEMORIA DEL MÓDULO, escrito porque el
 * paso 5 de la tarea pide decidirlo explícito:
 *
 * Con la navegación clásica de Shopify (PDP → carrito → otra PDP) la memoria del
 * módulo se pierde en cada pageview, así que memoria sola = **una llamada por
 * pageview**. El costo de red es aceptable; lo que no es aceptable es lo otro:
 * cada pageview volvería a pintar los fallbacks y después haría **saltar los
 * números en pantalla** cuando llega la respuesta. Con `sessionStorage` la segunda
 * página ya tiene el valor de la app de forma SÍNCRONA (`read()`) y no hay salto.
 *
 * Es una caché por pestaña y muere con ella, que es lo correcto para un valor de
 * 5 minutos. `localStorage` sobreviviría a la sesión y haría que un descuento viejo
 * apareciera mañana; no se usa.
 *
 * Cada acceso va envuelto en `try/catch` porque hay contextos donde el accessor
 * MISMO tira (Safari en privado, cookies de terceros bloqueadas, iframe sandbox).
 * Sin storage, esto degrada a memoria del módulo y nada más.
 *
 * @returns {{ value: GambaSubscriptionConfigValue, savedAt: number } | null}
 */
function readStorage() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!isRecord(parsed) || typeof parsed.savedAt !== 'number') return null;

    // Se re-valida la forma: un valor viejo o adulterado en storage no puede
    // envenenar la UI sólo por estar guardado.
    const value = normalize(parsed.value);
    if (!value) return null;

    return { value, savedAt: parsed.savedAt };
  } catch {
    return null;
  }
}

/** @param {{ value: GambaSubscriptionConfigValue, savedAt: number }} entry */
function writeStorage(entry) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // Sin persistencia queda la memoria del módulo. No es un error del usuario.
  }
}

/** Hidrata la memoria del módulo desde storage la primera vez que hace falta. */
function ensureCache() {
  cached ??= readStorage();
  return cached;
}

/* ---------------------------------------------------------------------------
   Normalización — la única puerta por la que entra un valor del endpoint
   --------------------------------------------------------------------------- */

/**
 * @param {unknown} value
 * @returns {value is Record<string, unknown>}
 */
function isRecord(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Primer alias presente, o `undefined`.
 *
 * @param {Record<string, unknown>} source
 * @param {readonly string[]} keys
 * @returns {unknown}
 */
function pick(source, keys) {
  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null) return source[key];
  }

  return undefined;
}

/**
 * Número dentro de rango, o `null`. Acepta el string `"10"` porque un backend que
 * serializa números como strings es lo más común del mundo.
 *
 * @param {unknown} value
 * @param {number} min
 * @param {number} max
 * @returns {number | null}
 */
function toNumberInRange(value, min, max) {
  const parsed = typeof value === 'string' ? Number(value.trim()) : value;
  if (typeof parsed !== 'number' || !Number.isFinite(parsed)) return null;
  if (parsed < min || parsed > max) return null;

  return parsed;
}

/**
 * Una frecuencia del endpoint → `{ count, label }`.
 *
 * Acepta las tres formas plausibles: el objeto (`{count, label}` y sus alias), el
 * número pelado (`2`) y el string numérico (`"2"`). Un label que no sea string con
 * texto queda en `null`, que es un estado válido y traducible por el consumidor —
 * no un `undefined` que termine impreso en pantalla.
 *
 * @param {unknown} entry
 * @returns {GambaFrequency | null}
 */
function normalizeFrequency(entry) {
  const rawCount = isRecord(entry) ? pick(entry, FIELDS.count) : entry;
  // 1 a 12 meses: el rango configurable de la app.
  const count = toNumberInRange(rawCount, 1, 12);
  if (count === null || !Number.isInteger(count)) return null;

  const rawLabel = isRecord(entry) ? pick(entry, FIELDS.label) : undefined;
  const label = typeof rawLabel === 'string' && rawLabel.trim() !== '' ? rawLabel.trim() : null;

  return { count, label };
}

/**
 * La respuesta del endpoint → la forma exacta de la API, o `null` si no se pudo
 * sacar NADA en limpio.
 *
 * Cada campo cae al fallback POR SEPARADO: una lista de frecuencias con forma
 * inesperada no tiene por qué tirar a la basura un descuento que vino bien.
 *
 * @param {unknown} raw
 * @returns {GambaSubscriptionConfigValue | null}
 */
function normalize(raw) {
  if (!isRecord(raw)) return null;

  // Se tolera el sobre `{ config: { ... } }` y también `{ data: { ... } }`.
  const nested = pick(raw, ['config', 'data', 'settings']);
  const data = isRecord(nested) ? nested : raw;

  const discount = toNumberInRange(pick(data, FIELDS.discount), 0, 100);
  /*
   * DE 0 A CIEN MILLONES DE CENTAVOS ($1.000.000). EL RANGO ES PARTE DEL
   * CAMBIO A MONTO, no un detalle: mientras el umbral fue en unidades esto
   * decía `1, 1000`, y un `4500000` cae fuera → `toNumberInRange` devuelve
   * `null` → el valor cae al fallback. Como el fallback tiene el mismo número
   * que la app trae de fábrica, el síntoma habría sido NINGUNO: la tienda
   * siguiendo la regla vieja sin un solo error en consola. Es el modo de falla
   * que el comentario de `FIELDS` describe, y acá es donde se dispara.
   *
   * El 0 pasa a propósito (la app apagando el beneficio); el que decide qué
   * hacer con él es cada consumidor.
   */
  const amount = toNumberInRange(pick(data, FIELDS.amount), 0, 100000000);
  // El tope va de 0 (sin tope) a diez millones de centavos, o sea $100.000: un
  // rango, y no `Number.MAX_SAFE_INTEGER`, porque un tope absurdo que llegue
  // por un error de la app no tiene que desactivar la validación del theme.
  const cap = toNumberInRange(pick(data, FIELDS.cap), 0, 10000000);

  const rawFrequencies = pick(data, FIELDS.frequencies);
  /** @type {GambaFrequency[]} */
  let frequencies = [];

  if (Array.isArray(rawFrequencies)) {
    /** @type {Map<number, GambaFrequency>} */
    const byCount = new Map();

    for (const entry of rawFrequencies) {
      const frequency = normalizeFrequency(entry);
      // El primero gana: si la app repite un count, el duplicado no pisa el label bueno.
      if (frequency && !byCount.has(frequency.count)) byCount.set(frequency.count, frequency);
    }

    frequencies = [...byCount.values()].sort((a, b) => a.count - b.count);
  }

  /*
   * LOS LABELS VIENEN EN UN OBJETO APARTE, indexado por count y con las claves
   * como STRING (`{"1":"Mensual"}`). Se superponen acá y no en
   * `normalizeFrequency` porque esa función ve un item por vez y el mapa es de
   * la respuesta entera.
   *
   * Un label del mapa NO pisa uno que ya vino en el item: si la app manda las
   * dos formas, gana la específica. Y una clave del mapa que no corresponde a
   * ninguna frecuencia habilitada se ignora sin ruido — que la app anuncie el
   * label de una frecuencia que no ofrece no es un error del theme.
   */
  const rawLabels = pick(data, FIELDS.frequencyLabels);
  if (frequencies.length > 0 && isRecord(rawLabels)) {
    frequencies = frequencies.map((frequency) => {
      if (frequency.label !== null) return frequency;
      const fromMap = rawLabels[String(frequency.count)];
      const label = typeof fromMap === 'string' && fromMap.trim() !== '' ? fromMap.trim() : null;
      return label === null ? frequency : { count: frequency.count, label };
    });
  }

  const gotSomething = discount !== null || amount !== null || cap !== null || frequencies.length > 0;
  if (!gotSomething) return null;

  /*
   * Congelado, igual que el fallback: el panel y el carrito comparten ESTE
   * objeto y esta caché. Un `frequencies.sort()` in place de un consumidor le
   * cambiaría el orden al otro sin que nadie se entere. Como los módulos son
   * strict mode, un intento de escritura tira TypeError, que es un bug ruidoso
   * en vez de una corrupción muda. El consumidor que necesite ordenar o mapear
   * copia primero: `[...config.frequencies]`.
   */
  return Object.freeze({
    discountPercent: discount ?? SUBSCRIPTION_CONFIG_FALLBACK.discountPercent,
    freeShippingAmountCents: amount ?? SUBSCRIPTION_CONFIG_FALLBACK.freeShippingAmountCents,
    maxShippingAmountCents: cap ?? SUBSCRIPTION_CONFIG_FALLBACK.maxShippingAmountCents,
    frequencies:
      frequencies.length > 0
        ? Object.freeze(frequencies.map((frequency) => Object.freeze(frequency)))
        : SUBSCRIPTION_CONFIG_FALLBACK.frequencies,
    source: 'app',
  });
}

/* ---------------------------------------------------------------------------
   Lectura
   --------------------------------------------------------------------------- */

/**
 * Lo mejor que se sabe HOY, sin red y sin promesas. Para el render inicial de un
 * consumidor que ya está corriendo JS.
 *
 * Devuelve el último valor conocido de la app **aunque esté vencido**: un valor de
 * la app de hace diez minutos es más cierto que el fallback horneado, y evita que
 * los números salten. El TTL decide si hay que volver a pedir (`load()`), no qué se
 * puede mostrar.
 *
 * Nunca tira. Nunca devuelve `undefined`.
 *
 * @returns {GambaSubscriptionConfigValue}
 */
export function readSubscriptionConfig() {
  const entry = ensureCache();

  return entry ? entry.value : SUBSCRIPTION_CONFIG_FALLBACK;
}

/**
 * Hidrata: caché fresca si hay, y si no, el endpoint.
 *
 * **NUNCA RECHAZA.** Cualquier fallo —red caída, timeout, 404 del App Proxy, un
 * 302 al `/password` que devuelve HTML, JSON con forma inesperada— resuelve con
 * `readSubscriptionConfig()`, o sea con el último valor de la app o con el
 * fallback. El consumidor puede hacer `.then(apply)` sin `.catch()` y la UI nunca
 * queda a medio pintar ni con un `undefined%` en pantalla.
 *
 * @returns {Promise<GambaSubscriptionConfigValue>}
 */
export function loadSubscriptionConfig() {
  const entry = ensureCache();
  if (entry && Date.now() - entry.savedAt < TTL_MS) return Promise.resolve(entry.value);

  // Un solo fetch por página aunque el panel y el carrito pidan a la vez.
  inFlight ??= fetchConfig()
    .then((value) => {
      if (!value) return readSubscriptionConfig();

      cached = { value, savedAt: Date.now() };
      writeStorage(cached);

      return value;
    })
    .catch(() => readSubscriptionConfig())
    .finally(() => {
      // El fallo NO se persiste: la próxima página vuelve a intentar. Sólo se
      // libera el candado en memoria.
      inFlight = null;
    });

  return inFlight;
}

/**
 * El fetch, aislado. Devuelve `null` en vez de tirar cuando la respuesta no sirve.
 *
 * @returns {Promise<GambaSubscriptionConfigValue | null>}
 */
async function fetchConfig() {
  // AbortController y no `AbortSignal.timeout`: el segundo es más nuevo y esto no
  // tiene capa de build que lo transpile.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetch(ENDPOINT, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      signal: controller.signal,
    });

    if (!response.ok) return null;

    /*
     * El chequeo de content-type NO es paranoia de manual: hoy la tienda tiene
     * password y una llamada al proxy termina en un 302 al `/password`, que
     * `fetch` sigue y devuelve como HTML con status 200. Sin este chequeo,
     * `response.json()` tira sobre el `<!doctype html>`. Con él, es un `null`
     * ordenado que cae al fallback.
     */
    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('json')) return null;

    const value = normalize(await response.json());

    if (!value && !warned) {
      warned = true;
      // El único ruido en consola de todo el módulo, y el que hace visible el
      // modo de falla silencioso de K-15 (ver FIELDS).
      console.warn(
        `[gamba] ${ENDPOINT} respondió JSON pero ninguna clave conocida coincidió: se usan los fallbacks. Revisar FIELDS en gamba-subscription-config.js.`
      );
    }

    return value;
  } catch {
    // Red caída, timeout/abort, JSON roto. El llamador ya sabe qué hacer.
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/* ---------------------------------------------------------------------------
   Etiquetas — lo único de este módulo que no habla con el endpoint
   --------------------------------------------------------------------------- */

/**
 * El centinela que Liquid deja en la forma `other` para que JS meta el número.
 *
 * VA SIN LLAVES (`__COUNT__`, no la versión con llaves dobles) y no es cosmético:
 * **un `}` adentro de un string en una salida de Liquid rompe el tokenizador y
 * Shopify RECHAZA el archivo entero**, con `shopify theme check` en cero offenses.
 * Es la misma familia de centinelas que ya usa el carrito (`__PERCENT__`). Si
 * cambia acá, cambia en los dos emisores: `snippets/gamba-cart-subscribe.liquid`
 * y `sections/gamba-main-product.liquid`.
 *
 * `__UNITS__` y `__THRESHOLD__` estaban en esta lista y se fueron con la D-14 de
 * la fase 7: alimentaban el aviso de "faltan N unidades para el envío gratis",
 * que vivía en la celda de envío del desglose, y esa celda pasó a texto fijo
 * (`gamba.subscription_shipping_pending`). Sus claves de locale se borraron en el
 * mismo commit; el único centinela del carrito que queda es `__PERCENT__`.
 */
export const COUNT_SENTINEL = '__COUNT__';

/**
 * La forma plural que le toca a un número, con el locale del documento.
 *
 * `Intl.PluralRules` y no un `count === 1` a secas: es la única forma correcta de
 * elegir entre `one` y `other` sin hornear la regla de un idioma, no necesita capa
 * de build y está en todos los navegadores que Horizon soporta. El locale sale de
 * `<html lang>`, que `layout/theme.liquid` pinta con `request.locale.iso_code`.
 *
 * DOS CAÍDAS, LAS DOS A `other`, y hay que saber por qué:
 *
 * - si `Intl.PluralRules` no existe o el locale es inválido (tira `RangeError`),
 *   se decide con `count === 1`, que es correcto para es y en, los dos idiomas que
 *   esta tienda tiene;
 * - si la categoría es `few`, `many`, `two` o `zero` —ru, pl, ar— cae a `other`,
 *   porque los locales de este theme sólo definen `one`/`other`. El día que la
 *   tienda agregue uno de esos idiomas, el arreglo es agregar las formas al locale
 *   Y leerlas acá; no es un bug latente escondido, es un límite escrito.
 *
 * @param {number} count
 * @returns {'one' | 'other'}
 */
function pluralCategory(count) {
  try {
    const locale = document.documentElement.lang || undefined;

    return new Intl.PluralRules(locale).select(count) === 'one' ? 'one' : 'other';
  } catch {
    return count === 1 ? 'one' : 'other';
  }
}

/**
 * "Cada 3 meses" / "3 meses" — la etiqueta de una frecuencia, armada con el número
 * del endpoint y las palabras de Liquid.
 *
 * POR QUÉ VIVE ACÁ Y NO EN CADA CONSUMIDOR: el carrito y el panel de la PDP
 * necesitan exactamente esto y con la misma regla, y este módulo ya es el dueño del
 * contrato de frecuencias — su encabezado dice "con `label: null` el consumidor
 * traduce a partir de `count`" y hasta hoy no le daba con qué. Es la pieza que
 * faltaba de ese contrato, no una utilidad nueva.
 *
 * Los dos templates los emite el consumidor UNA vez, desde su propia clave de
 * locale (el carrito usa `gamba.subscription_every_months`, "Cada mes"/"Cada N
 * meses"; el panel usa `gamba.purchase_months`, "N mes"/"N meses"). Este módulo no
 * conoce ninguna clave: recibe dos strings y elige.
 *
 * La forma `one` llega ya cerrada (Liquid la pinta con `count: 1`, y en es/en la
 * categoría `one` sólo le toca al 1), así que el centinela en general sólo aparece
 * en la forma `other`. El reemplazo se hace en las dos igual: es idempotente y
 * cubre un locale que sí interpole el número en singular.
 *
 * @param {number} count - El número de meses. Entero.
 * @param {{ one?: string | null, other?: string | null }} templates - Los dos strings de Liquid.
 * @returns {string | null} La etiqueta, o `null` si no hay con qué armarla.
 */
export function monthsLabel(count, templates) {
  if (!Number.isFinite(count)) return null;
  if (!isRecord(templates)) return null;

  const one = typeof templates.one === 'string' ? templates.one : '';
  const other = typeof templates.other === 'string' ? templates.other : '';

  // Si falta una forma se usa la que hay: media etiqueta es mejor que ninguna, y
  // un chip sin texto es un control que nadie puede leer.
  const preferred = pluralCategory(count) === 'one' ? one : other;
  const template = preferred || other || one;
  if (template === '') return null;

  return template.replaceAll(COUNT_SENTINEL, String(count));
}

/* ---------------------------------------------------------------------------
   Instalación
   --------------------------------------------------------------------------- */

/*
 * El global es la forma de consumo de HOY, por lo que está explicado en el
 * encabezado: sin entrada en el importmap no hay especificador que importar, y un
 * import relativo pierde el `?v=` del `asset_url` y se queda cacheado un año. El
 * theme ya expone `Theme` así (`snippets/scripts.liquid`), o sea que no es un
 * patrón ajeno.
 *
 * Idempotente, igual que el `customElements.get(...)` de los otros módulos gamba:
 * si dos secciones emiten el tag, la segunda no pisa la caché de la primera.
 */
if (!window.GambaSubscriptionConfig) {
  window.GambaSubscriptionConfig = Object.freeze({
    fallback: SUBSCRIPTION_CONFIG_FALLBACK,
    read: readSubscriptionConfig,
    load: loadSubscriptionConfig,
    // Aditivo: los tres de arriba no cambiaron de firma ni de forma. Los
    // consumidores lo llaman con `?.monthsLabel?.(…)` y tienen su propia caída,
    // así que un theme a medio deployar no se queda sin chips.
    monthsLabel,
  });
}
